import { useCallback, useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Tag } from 'primereact/tag';
import {
  Resolver,
  SubmitHandler,
  useForm,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useFeatureFlag } from '@/app/services/features';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import usePageTitle from '@/components/hooks/usePageTitle';
import { useSaveShipment } from '@/services/shipping/hooks';
import type { ShipmentDetail } from '@/services/shipping/types';
import type { ShippingAccountSearchFragment } from '@/types.gql';

import { useShipmentContext } from '../../hooks/useShipmentContext';
import { useShipmentStatusOptions } from '../../hooks/useShipmentStatusOptions';
import { getUpdateShipmentInput } from '../../utils';
import { createShipmentSchema, ShipmentSchema } from '../../utils/schema';
import { ShipmentToolBar } from '../ShipmentToolbar';
import { BaseShipmentForm } from './BaseShipmentForm';

export const ShipmentForm: React.FC = () => {
  const { t } = useTranslation();
  const { setTitle } = usePageTitle();
  const dispatch = useDispatch();
  const { shipment, calves, donors, embryos, recipients, semen } =
    useShipmentContext();
  const shipmentId = shipment.id;

  const { mutateAsync: saveShipment, isPending: updateShipmentLoading } =
    useSaveShipment();

  const allowIncompleteMismatch = useFeatureFlag(
    'Shipping_Allow_Incomplete_Mismatch',
  );
  const isShipToAutofillEnabled = useFeatureFlag(
    'Shipping_ShipTo_Address_Autofill',
  );
  const isShippingMethodRequired = useFeatureFlag('Shipping_Method_Required');

  const shipmentResolver: Resolver<ShipmentSchema> = (
    values,
    context,
    options,
  ) =>
    zodResolver(
      createShipmentSchema({
        hasCalves: !!calves.length,
        hasDonors: !!donors.length,
        hasEmbryos: !!embryos.length,
        hasRecipients: !!recipients.length,
        hasSemen: !!semen.length,
        enforceContentMatch: !allowIncompleteMismatch,
        requireShippingMethod: isShippingMethodRequired,
      }),
    )(values, context, options);
  const { handleSubmit, control, formState, reset, setValue, watch } =
    useForm<ShipmentSchema>({
      resolver: shipmentResolver,
      defaultValues: shipment as any,
      disabled: shipment.stateCode !== 0,
    });

  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    setIsFormDirty(formState.isDirty);
  }, [formState.isDirty]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (shipment) {
      reset(shipment as any);
      setTimeout(() => {
        setTitle(shipment?.shipmentNumber || '', '');
      }, 500);
    }
  }, [reset, shipment]);

  const setShipmentValue: UseFormSetValue<ShipmentSchema> = useCallback(
    (fieldName, fieldValue, options) => {
      setValue(fieldName, fieldValue, options);

      if (
        !isShipToAutofillEnabled ||
        fieldName !== 'shipTo' ||
        !fieldValue ||
        typeof fieldValue !== 'object' ||
        !('id' in fieldValue)
      ) {
        return;
      }

      const account = fieldValue as ShippingAccountSearchFragment;
      const ranchAddress = account.ranchAddress;
      setValue('shipName', account.name ?? null);
      setValue('shipAddress', ranchAddress?.line1 ?? null);
      setValue('shipAddress2', ranchAddress?.line2 ?? null);
      setValue('shipAddress3', ranchAddress?.line3 ?? null);
      setValue('shipCity', ranchAddress?.city ?? null);
      setValue('shipState', ranchAddress?.stateOrProvince ?? null);
      setValue('shipZip', ranchAddress?.postalCode ?? null);
    },
    [isShipToAutofillEnabled, setValue],
  );

  const onSubmit = useCallback<SubmitHandler<ShipmentSchema>>(
    async (data) => {
      try {
        const updatedData = {
          ...data,
          owner:
            typeof data.owner === 'object' && data.owner
              ? data.owner.id
              : data.owner,
          packedBy:
            typeof data.packedBy === 'object' && data.packedBy
              ? data.packedBy.id
              : data.packedBy,
          shipFromSite:
            typeof data.shipFromSite === 'object' && data.shipFromSite
              ? data.shipFromSite.id
              : data.shipFromSite,
        };

        const shipmentInput = getUpdateShipmentInput(
          updatedData as unknown as ShipmentDetail,
        );

        await saveShipment(shipmentId, shipmentInput);

        await queryClient.invalidateQueries({
          queryKey: ['shipping'],
        });
      } catch (error: any) {
        const message =
          error?.message || 'An error occurred while saving. Please try again.';
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: message,
            life: ToastLife.LONG,
          }),
        );
      }
    },
    [queryClient, shipmentId, dispatch, t, saveShipment],
  );

  const shipmentStatus = useShipmentStatusOptions(shipment?.owner);

  const currentStatus = watch('shipmentStatus');
  const shipmentInProgress = currentStatus === 100000002;

  return (
    <>
      <ShipmentToolBar
        setValue={setValue}
        onSubmit={handleSubmit(onSubmit)}
        isDirty={isFormDirty}
        disableSave={!isFormDirty}
        loading={updateShipmentLoading}
      />
      <BaseShipmentForm
        control={control}
        handleSubmit={handleSubmit}
        setValue={setShipmentValue}
        watch={watch}
        onSubmit={onSubmit}
        shipmentStatus={shipmentStatus}
        shipmentInProgress={shipmentInProgress}
        additionalContent={
          <div className="label-input-group">
            <label htmlFor="state" id="state-label">
              {t('fields.state')}
            </label>
            <div className="flex items-center">
              <Tag
                value={
                  shipment.stateCode === 0
                    ? t('common.active')
                    : t('common.inactive')
                }
                severity={shipment.stateCode === 0 ? 'success' : 'danger'}
              />
            </div>
          </div>
        }
      />
    </>
  );
};
